import { isEmpty, isUndefined } from 'lodash';
import Router from 'next/router';
import { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { TreeItem, TreeItemButton } from '@air/component-tree';
import { GlobeIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Tooltip } from '@air/primitive-tooltip';

import { BoardsTree } from '~/components/CurrentWorkspaceNav/BoardsNav/BoardsTree/BoardsTree';
import { useGeneralLibraryTitle } from '~/components/LibraryBeta/hooks/useGeneralLibraryTitle';
import { GeneralLibraryIcon } from '~/components/LibraryBeta/LibraryNavigation/components/LibraryNavigationGeneralItem/GeneralLibraryIcon';
import { GeneralLibraryItemActions } from '~/components/LibraryBeta/LibraryNavigation/components/LibraryNavigationGeneralItem/GeneralLibraryItemActions';
import { DNDBoardsRoot } from '~/components/Shared/Drag/DNDBoardsRoot';
import { Routes } from '~/constants/routes';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useWorkspaceBoardRearrange } from '~/hooks/useWorkspaceBoardRearrange';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { isUrlActiveRouteSelector } from '~/store/router/selectors';
import { useWorkspaceBoards } from '~/swr-hooks/boards/useWorkspaceBoards';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
import { useAirSelector } from '~/utils/ReduxUtils';

export const LibraryNavigationGeneralItem = memo(() => {
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(undefined);
  const { currentWorkspace } = useCurrentWorkspace();
  const { generalLibraryTitle } = useGeneralLibraryTitle();
  const {
    data: workspaceBoards,
    isLoadingMore,
    hasMore,
    isInitialLoading,
    loadNextPage,
  } = useWorkspaceBoards({ workspaceId: currentWorkspace?.id, view: 'root' });
  const centralizedBoard = useSelector(centralizedBoardSelector);
  const hasUserExplicitlyExpandedOrCollapsed = !isUndefined(isExpanded);
  const shouldShowBoards = isExpanded && !isEmpty(workspaceBoards);

  const isActive = useAirSelector((st) => isUrlActiveRouteSelector(st, Routes.generalLibrary));

  useFetchObjectsPermissions({
    objects: {
      boardIds: workspaceBoards.map((board) => board.id),
    },
  });

  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { onBoardRearrange, getCanRearrange } = useWorkspaceBoardRearrange({ workspacePermissions: permissions });
  const canCreateRootBoards = canCreateBoard(permissions);

  /**
   * Automatically expand the general library if the centralized board is not
   * in a library and the user has not explicitly expanded or collapsed the
   * library since all non-library boards will exist under the general library.
   */
  useEffect(() => {
    if (
      (isActive || !centralizedBoard?.library || workspaceBoards?.some((board) => board.id === centralizedBoard?.id)) &&
      !hasUserExplicitlyExpandedOrCollapsed
    ) {
      setIsExpanded((prevState) => !prevState);
    }
  }, [centralizedBoard, hasUserExplicitlyExpandedOrCollapsed, isActive, workspaceBoards]);

  const suffix = useMemo(() => <GeneralLibraryItemActions />, []);

  const treeItemButton = useMemo(
    () => (
      // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
      <TreeItemButton data-testid="GENERAL_LIBRARY_NAV_ITEM" onClick={() => Router.push(Routes.generalLibrary)}>
        <div className="flex items-center gap-1">
          <span className="truncate">{generalLibraryTitle}</span>
          <Tooltip
            label={`Users in ${currentWorkspace?.name} are automatically members of this library`}
            side="left"
            align="center"
          >
            <GlobeIcon size="sm" className="shrink-0 text-gray-8" />
          </Tooltip>
        </div>
      </TreeItemButton>
    ),
    [generalLibraryTitle, currentWorkspace?.name],
  );

  // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
  if (!workspaceBoards.length && !canCreateRootBoards) return null;

  return (
    <div>
      <DNDBoardsRoot>
        <TreeItem
          defaultOpen={false}
          onOpenChange={setIsExpanded}
          open={isExpanded}
          prefix={<GeneralLibraryIcon />}
          suffix={suffix}
          state={isActive ? 'active' : 'default'}
        >
          {treeItemButton}
        </TreeItem>
      </DNDBoardsRoot>

      {shouldShowBoards && (
        <div className="ml-[28px]">
          <BoardsTree
            enableAutoExpand={true}
            boardType="library"
            boards={workspaceBoards}
            parentBoardId={null}
            onBoardRearrange={onBoardRearrange}
            getCanRearrange={getCanRearrange}
          />

          {hasMore && !isInitialLoading && (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <Button appearance="ghost" color="gray" onClick={() => loadNextPage()} size="small">
              {isLoadingMore ? 'Loading...' : 'Show more'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
});

LibraryNavigationGeneralItem.displayName = 'LibraryNavigationGeneralItem';
